import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Card,
  Table,
  Tag,
  Button,
  Space,
  Typography,
  Modal,
  Select,
  Input,
  DatePicker,
  Form,
  Image,
  Descriptions,
  Divider,
  message,
  Tabs,
} from "antd";
import UserProfileModal from "../components/UserProfileModal";
import MerchantProfileModal from "../components/MerchantProfileModal";
import lang from "../helper/lang";
import {
  EyeOutlined,
  IdcardOutlined,
  FileSearchOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  FilterFilled,
  CheckOutlined,
  StarOutlined,
  UploadOutlined,
  EditOutlined,
} from "@ant-design/icons";
import moment from "moment";
import useRequest from "../hooks/useRequest";
import { Severty, ShowToast } from "../helper/toast";
import "./KYCManagement.css";
const { Text } = Typography;
const { Search } = Input;


function KYCManager() {
  const { request } = useRequest();
  const inFlightRequests = useRef(new Set());
  const [activeTab, setActiveTab] = useState("kyc");
  const [kycList, setKycList] = useState([]);
  const [kybList, setKybList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const [viewModal, setViewModal] = useState(false);
  const [documentModal, setDocumentModal] = useState(false);
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [reviewModal, setReviewModal] = useState(false);
  const [kybApproveModal, setKybApproveModal] = useState(false);
  const [registrationDoc, setRegistrationDoc] = useState(null);
  const [makerModal, setMakerModal] = useState(false);
  const [makerModalType, setMakerModalType] = useState("kyc"); // "kyc" | "kyb"
  const [makerForm] = Form.useForm();

  const [searchText, setSearchText] = useState("");
  const [filters, setFilters] = useState({ status: null });
  const [debouncedSearch, setDebouncedSearch] = useState(searchText);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const [form] = Form.useForm();
  const [filtersVisible, setFiltersVisible] = useState(true);
  const [profileModalVisible, setProfileModalVisible] = useState(false);
  const [profileModalType, setProfileModalType] = useState("view");
  const [profileUser, setProfileUser] = useState(null);
  const moduleAccess = {
    can_view: true,
    can_create: true,
    can_edit: true,
    can_delete: true,
  };
  const [currentAdminRole, setCurrentAdminRole] = useState(moduleAccess);

  const mapStatusToBoolean = (status) => {
    return status === "Active" || status === "active";
  };

  useEffect(() => {
    try {
      const storedUserProfile = localStorage.getItem("user");
      const parsedProfile = storedUserProfile ? JSON.parse(storedUserProfile) : null;
      if (parsedProfile?.role?.module_permissions) {
        // Map ownerType to actual module name in permissions
        const moduleName = "kyc_kyb";

        const kycKybModule = parsedProfile?.role?.module_permissions?.find(item => item.module === moduleName);
        if (kycKybModule) {
          setCurrentAdminRole(kycKybModule);
        } else {
          setCurrentAdminRole(moduleAccess);
        }
      }
    } catch (error) {
      ShowToast("Failed to fetch KYC/KYB roles", Severty.ERROR);
      setCurrentAdminRole(moduleAccess);
    }
  }, []);

  const handleViewProfile = (record) => {
    if (!currentAdminRole?.can_view) {
      ShowToast("You are not authorized to view KYC/KYB", Severty.ERROR);
      return;
    }
    const id = activeTab === "kyc" ? record?.user?.id : record?.id;
    const endpoint = activeTab === "kyc" ? "users" : "merchant-profile";
    request({
      url: `/api/admin/${endpoint}/${id}`,
      method: "GET",
      onSuccess: (res) => {
        setProfileUser(res.data);
        setProfileModalType("view");
        setProfileModalVisible(true);
      },
      onError: (error) => {
        ShowToast(error?.response?.data?.message || error.message, Severty.ERROR);
      },
    });
  };

  const runWithRequestLock = useCallback((requestKey, requestExecutor) => {
    if (!currentAdminRole?.can_view) {
      ShowToast("You are not authorized to view KYC/KYB", Severty.ERROR);
      return false;
    }
    if (inFlightRequests.current.has(requestKey)) {
      return false;
    }

    inFlightRequests.current.add(requestKey);

    const releaseLock = () => {
      inFlightRequests.current.delete(requestKey);
    };

    requestExecutor(releaseLock);
    return true;
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchText);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchText]);

  const fetchKyc = useCallback(() => {
    if (!currentAdminRole?.can_view) {
      ShowToast("You are not authorized to view KYC/KYB", Severty.ERROR);
      return;
    }
    const requestKey = `fetch-${activeTab}-${pagination.current}-${pagination.pageSize}-${debouncedSearch}-${filters.status ?? ""}`;
    const hasStarted = runWithRequestLock(requestKey, (releaseLock) => {
      setLoading(true);
      // const res = await mockApi({
      //   page: pagination.current,
      //   limit: pagination.pageSize,
      //   search: searchText,
      //   status: filters.status,
      // });

      request({
        url: `api/admin/${activeTab}?page=${pagination.current}&limit=${pagination.pageSize}&search=${encodeURIComponent(debouncedSearch)}&status=${encodeURIComponent(filters.status ?? "")}`,
        method: "GET",
        onSuccess: (res) => {
          if (activeTab === "kyc") {
            setKycList(res?.data || []);
          } else {
            setKybList(res?.data || []);
          }
          // Only sync totals from the API. Do not overwrite current/pageSize here:
          // the server may return them as strings while state uses numbers, which would
          // change useCallback deps and immediately trigger a duplicate fetch.
          setPagination((prev) => ({
            ...prev,
            total: res?.pagination?.total ?? 0,
            totalPages: res?.pagination?.totalPages ?? prev.totalPages,
          }));
          setLoading(false);
          releaseLock();

        },
        onError: (error) => {
          setLoading(false);
          if (activeTab === "kyc") {
            setKycList([]);
          } else {
            setKybList([]);
          }
          setPagination((prev) => ({ ...prev, total: 0 }));
          ShowToast("Failed to fetch KYC/KYB", Severty.ERROR);
          releaseLock();
        },
      });
    });

    if (!hasStarted) {
      return;
    }
  }, [debouncedSearch, pagination.current, pagination.pageSize, filters, activeTab]);

  useEffect(() => {
    fetchKyc();
  }, [fetchKyc]);

  // -----------------------------
  // STATUS COLOR MAP
  // -----------------------------
  const statusColor = {
    PENDING: "orange",
    UNDER_REVIEW: "blue",
    COMPLETED: "green",
    REJECTED: "red",
    CANCELLED: "volcano",
  };

  // -----------------------------
  // TABLE COLUMNS
  // -----------------------------
  const KYCColumns = [
    {
      title: "User ID",
      dataIndex: "user_id",
      width: 120,
      render: (_, record) => (
        <Text
          code
          style={{ cursor: "pointer", color: "#1890ff" }}
          onClick={() => handleViewProfile(record)}
        >
          {record?.user?.id}
        </Text>
      ),
    },
    {
      title: "Full Name",
      dataIndex: ["first_name", "middle_name", "last_name"],
      width: 180,
      render: (_, record) => <Text>{record?.user?.first_name} {record?.user?.middle_name} {record?.user?.last_name}</Text>,
    },
    {
      title: "Email / Mobile",
      width: 250,
      render: (_, record) => (
        <>
          <div>{record?.user?.email}</div>
          <Text type="secondary">{record?.user?.mobile}</Text>
        </>
      ),
    },
    {
      title: "Document",
      dataIndex: "document_submitted",
      width: 160,
      render: (_, record) => <Text>{record?.document_submitted ?? "-"}</Text>,
    },
    {
      title: "Submission",
      dataIndex: "submitted_at",
      width: 140,
      render: (d) => moment(d).format("DD MMM YYYY"),
    },
    // {
    //   title: "Expiry",
    //   dataIndex: "expiry_date",
    //   width: 140,
    //   render: (d) => (d ? moment(d).format("DD MMM YYYY") : "-"),
    // },
    {
      title: "Status",
      dataIndex: "status",
      width: 150,
      render: (status) => <Tag color={statusColor[status]}>{status.includes("_") ? status.split("_").join(" ") : status}</Tag>,
    },
    {
      title: "Actions",
      width: 220,
      render: (_, record) => (
        <Space>
          {currentAdminRole?.can_view && <Button
            className="button-action-small"
            onClick={() => {
              setSelectedUser(record);
              setViewModal(true);
            }}
          >
            <EyeOutlined />
          </Button>}

          {currentAdminRole?.can_view && <Button
            className="button-action-small"
            onClick={() => {
              // setSelectedUser(record);
              setSelectedDocuments(record?.documents || []);
              setDocumentModal(true);
            }}
          >
            <FileSearchOutlined />
          </Button>}

          {record.status == "UNDER_REVIEW" && currentAdminRole?.can_maker && (
            <Button
              className="button-action-small"
              type={record.maker_decision ? "dashed" : "default"}
              title={record.maker_decision ? "View Reviewer Recommendation" : "Add Reviewer Review"}
              onClick={() => handleMarkUnderReview(record)}
            >
              {record.maker_decision ? <CheckCircleOutlined style={{ color: "#52c41a" }} /> : <EditOutlined />}
            </Button>
          )}

          {record.status == "UNDER_REVIEW" && (
            currentAdminRole?.can_checker && <Button
              className="button-action-small"
              type="primary"
              title="Approve / Reject"
              onClick={() => {
                setSelectedUser(record);
                setReviewModal(true);
              }}
            >
              <StarOutlined />
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const KYBColumns = [
    {
      title: "Merchant ID",
      dataIndex: "id",
      width: 120,
      render: (id, record) => (
        <Text
          code
          style={{ cursor: "pointer", color: "#1890ff" }}
          onClick={() => handleViewProfile(record)}
        >
          {id}
        </Text>
      ),
    },
    {
      title: "Business Number",
      dataIndex: "merchant_code",
      width: 120,
      render: (merchant_code) => <Text code>{merchant_code}</Text>,
    },
    {
      title: "Business Name",
      dataIndex: "business_name",
      width: 120,
      render: (business_name) => <Text >{business_name}</Text>,
    },
    {
      title: "Business Type",
      dataIndex: "business_type",
      width: 120,
      render: (business_type) => <Text >{business_type}</Text>,
    },
    {
      title: "KYB Status",
      dataIndex: "kyb_status",
      width: 120,
      render: (kyb_status) => <Tag color={statusColor[kyb_status]}>{kyb_status}</Tag>,
    },
    {
      title: "KYB Date",
      dataIndex: "kyb_approved_at",
      width: 120,
      render: (d) => d ? moment(d).format("DD MMM YYYY") : "-",
    },
    {
      title: "Actions",
      width: 220,
      render: (_, record) => (
        <Space>
          {currentAdminRole?.can_view && <Button
            className="button-action-small"
            onClick={() => {
              setSelectedUser(record);
              setViewModal(true);
            }}
          >
            <EyeOutlined />
          </Button>}

          {["PENDING", "UNDER_REVIEW"].includes(record.kyb_status) && currentAdminRole?.can_maker && (
            <Button
              className="button-action-small"
              type={record.maker_decision ? "dashed" : "default"}
              title={record.maker_decision ? "View Reviewer Recommendation" : "Mark Under Review"}
              onClick={() => handleKybMarkUnderReview(record)}
            >
              {record.maker_decision ? <CheckCircleOutlined style={{ color: "#52c41a" }} /> : <EditOutlined />}
            </Button>
          )}

          {["PENDING", "UNDER_REVIEW"].includes(record.kyb_status) && currentAdminRole?.can_checker && (
            <Button
              className="button-action-small"
              type="primary"
              title="Approve Merchant"
              onClick={() => {
                setSelectedUser(record);
                setKybApproveModal(true);
              }}
            >
              <CheckOutlined />
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const columns = activeTab === "kyc" ? KYCColumns : KYBColumns;

  // TABLE COLUMNS
  // -----------------------------
  const documentColumns = [
    {
      title: "Document Role",
      dataIndex: "doc_role",
      width: 120,
      render: (_, record) => <Text code>{record?.doc_role}</Text>,
    },
    {
      title: "Document Name",
      dataIndex: "doc_step_name",
      width: 180,
      render: (_, record) => <Text>{record?.doc_step_name}</Text>,
    },
    {
      title: "Preview",
      dataIndex: "url",
      width: 150,
      render: (url) => (
        <Image
          src={url}
          alt="document"
          width={60}   // small thumbnail
          height={60}
          style={{ objectFit: "cover", borderRadius: 6 }}
        />
      ),
    },
  ];

  // -----------------------------
  // REVIEW SUBMIT
  // -----------------------------
  const handleMarkUnderReview = (record) => {
    setSelectedUser(record);
    setMakerModalType("kyc");
    setMakerModal(true);
    makerForm.setFieldsValue({
      maker_decision: record.maker_decision,
      maker_comment: record.maker_comment,
    });
  };

  const handleKybMarkUnderReview = (record) => {
    setSelectedUser(record);
    setMakerModalType("kyb");
    setMakerModal(true);
    makerForm.setFieldsValue({
      maker_decision: record.maker_decision,
      maker_comment: record.maker_comment,
    });
  };

  const handleMakerSubmit = (values) => {
    const { maker_decision, maker_comment } = values;
    const isKyc = makerModalType === "kyc";
    const url = isKyc
      ? `api/admin/kyc/${selectedUser?.id}/review`
      : `api/admin/merchant-profile/${selectedUser?.id}/review`;

    request({
      url,
      method: "POST",
      data: { maker_decision, maker_comment },
      onSuccess: () => {
        ShowToast(
          isKyc ? "Reviewer recommendation saved" : "Merchant KYB marked as under review",
          Severty.SUCCESS
        );
        setMakerModal(false);
        makerForm.resetFields();
        fetchKyc();
      },
      onError: (err) => {
        ShowToast(err?.response?.data?.message || "Failed to update status", Severty.ERROR);
      }
    });
  };
  const handleReviewSubmit = (values) => {
    let decision = values.decision;
    let reason = values.reason;
    if (decision === "Completed" || (decision === "Rejected" && reason && reason.trim())) {
      const requestKey = `review-${selectedUser?.id}-${decision}`;
      runWithRequestLock(requestKey, (releaseLock) => request({
        url: `api/admin/kyc/${selectedUser?.id}/${decision === "Completed" ? "complete" : "reject"}`,
        method: "POST",
        data: { reason: reason },
        onSuccess: (res) => {
          message.success(decision === "Completed" ? "KYC Completed Successfully" : "KYC Rejected Successfully");
          setReviewModal(false);
          form.resetFields();
          fetchKyc();
          releaseLock();
        },
        onError: () => {
          releaseLock();
        },
      }));
    } else {
      message.warning("KYC Rejected");
    }
    setReviewModal(false);
    form.resetFields();
  };

  const handleKybApprove = () => {
    // Use FormData only when a file is selected (forces multipart/form-data).
    // Without a file, send plain JSON so the EncryptionMiddleware processes it correctly.
    let data;
    if (registrationDoc) {
      data = new FormData();
      data.append("registration_doc", registrationDoc);
    } else {
      data = {};
    }

    request({
      url: `/api/admin/merchant-profile/${selectedUser?.id}/approve`,
      method: "POST",
      data,
      onSuccess: (res) => {
        ShowToast("Merchant KYB Approved Successfully", Severty.SUCCESS);
        setKybApproveModal(false);
        setRegistrationDoc(null);
        fetchKyc();
      },
      onError: (err) => {
        ShowToast(err?.response?.data?.message || "Failed to approve KYB", Severty.ERROR);
      }
    });
  };

  return (
    <div>
      <Card
        title={
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 12,
            }}
          >
            <span>
              <IdcardOutlined /> {activeTab === "kyc" ? "KYC Management" : "KYB Management"}
            </span>
            <Button
              icon={<FilterFilled />}
              onClick={() => setFiltersVisible((prev) => !prev)}
            >
              {filtersVisible ? "Hide Filters" : "Show Filters"}
            </Button>
          </div>
        }
      >
        <Tabs
          activeKey={activeTab}
          onChange={(key) => {
            setActiveTab(key);
            setFilters({ status: null });
            setSearchText("");
            setDebouncedSearch("");
            setPagination((prev) => ({ ...prev, current: 1 }));
          }}
          items={[
            { key: "kyc", label: "KYC" },
            { key: "kyb", label: "KYB" },
          ]}
        />
        {filtersVisible && (
          <div style={{ marginBottom: 16 }}>
            <Space
              direction="horizontal"
              size="middle"
              style={{ width: "100%", flexWrap: "wrap" }}
            >
              <Search
                placeholder={activeTab === "kyc" ? "Search by Name / Email" : "Search by Business Name / Business Number"}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                style={{ minWidth: 220 }}
              />

              <Select
                placeholder="Filter Status"
                allowClear
                value={filters.status}
                style={{ minWidth: 180 }}
                onChange={(v) =>
                  setFilters((prev) => ({ ...prev, status: v }))
                }
              >
                {Object.keys(statusColor).map((s) => (
                  <Select.Option key={s}>{s}</Select.Option>
                ))}
              </Select>
            </Space>
          </div>
        )}
        <div className="table-responsive">
          <Table
            columns={columns}
            dataSource={activeTab === "kyc" ? kycList : kybList}
            rowKey="id"
            loading={loading}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "30", "50"],
              showTotal: (total, range) =>
                `Showing ${range[0]}-${range[1]} of ${total} records`,
            }}
            onChange={(p) =>
              setPagination({
                current: p.current,
                pageSize: p.pageSize,
                total: pagination.total,
              })
            }
          />
        </div>
      </Card>

      {/* VIEW DETAILS MODAL */}
      <Modal
        open={viewModal}
        onCancel={() => setViewModal(false)}
        footer={null}
        width={600}
        title={activeTab === "kyc" ? "KYC Details" : "KYB Details"}
      >
        {selectedUser && (
          activeTab === "kyc" ? <>
            <Descriptions column={1} bordered >
              <Descriptions.Item label="User ID">
                {selectedUser?.user?.id}
              </Descriptions.Item>
              <Descriptions.Item label="Full Name">
                {selectedUser?.user?.pronoun} {selectedUser?.user?.first_name} {selectedUser?.user?.middle_name} {selectedUser?.user?.last_name}
              </Descriptions.Item>
              <Descriptions.Item label="Email">
                {selectedUser?.user?.email}
              </Descriptions.Item>
              <Descriptions.Item label="Mobile">
                {selectedUser?.user?.country_code} {selectedUser?.user?.mobile}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={statusColor[selectedUser?.status]}>
                  {selectedUser?.status}
                </Tag>
              </Descriptions.Item>
              {selectedUser?.status == "REJECTED" && <Descriptions.Item label="Rejection Reason" style={{ wordBreak: "break-word", whiteSpace: "pre-wrap" }}>
                {selectedUser?.rejection_reason || "-"}
              </Descriptions.Item>}
              <Descriptions.Item label="Email Verified">
                {selectedUser?.user?.email_verified ? "Yes" : "No"}
              </Descriptions.Item>
              <Descriptions.Item label="Mobile Verified">
                {selectedUser?.user?.mobile ? "Yes" : "No"}
              </Descriptions.Item>
              <Descriptions.Item label="Submitted Date">
                {moment(selectedUser?.submitted_at).format("DD MMM YYYY, hh:mm A")}
              </Descriptions.Item>
              <Descriptions.Item label="GBG Reference ID">
                {selectedUser?.gbg_reference || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Joined Date">
                {moment(selectedUser?.user?.join_date).format("DD MMM YYYY, hh:mm A")}
              </Descriptions.Item>
              <Descriptions.Item label="Document Type">
                <Space>
                  {selectedUser?.document_submitted}

                  <Button
                    className="button-action-small"
                    onClick={() => {
                      setViewModal(false);
                      setSelectedDocuments(selectedUser?.documents || []);
                      setDocumentModal(true);
                    }}
                  >
                    <FileSearchOutlined />
                  </Button>
                </Space>
              </Descriptions.Item>
            </Descriptions>
          </> :
            <div className="kyb-profile-container">
              <div className="kyb-profile-cover-container12">
                <div className="kyb-profile-cover">
                  {selectedUser?.business_cover_img && (
                    <Image
                      src={selectedUser?.business_cover_img}
                      alt="Business Cover"
                      // className="kyb-profile-cover-image"
                      preview
                    />
                  )}
                  <div className="kyb-profile-cover-overlay" />
                </div>
                <div className="kyb-profile-avatar">
                  {selectedUser?.business_logo ? (
                    <Image
                      src={selectedUser?.business_logo}
                      alt="Business Logo"
                      className="kyb-profile-avatar-image"
                      preview
                    />
                  ) : (
                    <div className="kyb-profile-avatar-fallback">
                      {selectedUser?.business_name?.charAt(0) || "M"}
                    </div>
                  )}
                </div>
              </div>

              <div className="kyb-profile-header">
                <div className="kyb-profile-name">
                  {selectedUser?.business_name || "-"}
                </div>
                <Text type="secondary">
                  {selectedUser?.business_type || "Business Type Not Available"}
                </Text>
              </div>

              <Descriptions column={1} bordered>
                <Descriptions.Item label="Merchant ID">
                  {selectedUser?.id || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Business Code">
                  {selectedUser?.merchant_code || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Business Name">
                  {selectedUser?.business_name || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Business Type">
                  {selectedUser?.business_type || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Invoicing Name">
                  {selectedUser?.invoicing_name || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="GST Number">
                  {selectedUser?.gst_number || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Invoicing Email">
                  {selectedUser?.invoicing_email || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Active Status">
                  <Tag color={selectedUser?.is_active ? "green" : "red"}>
                    {selectedUser?.is_active ? "Active" : "Inactive"}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="KYB Status">
                  <Tag color={statusColor[selectedUser?.kyb_status] || "default"}>
                    {selectedUser?.kyb_status || "-"}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="KYB Date">
                  {selectedUser?.kyb_approved_at
                    ? moment(selectedUser.kyb_approved_at).format("DD MMM YYYY, hh:mm A")
                    : "-"}
                </Descriptions.Item>
                {/* <Descriptions.Item label="GBG Reference ID">
                  {selectedUser?.gbg_reference || "-"}
                </Descriptions.Item> */}
                {selectedUser?.registration_doc && (
                  <Descriptions.Item label="Registration Document">
                    <Image
                      src={selectedUser?.registration_doc}
                      alt="Registration Document"
                      width={80}
                      height={80}
                      className="kyb-registration-doc-image"
                    />
                  </Descriptions.Item>
                )}
              </Descriptions>
            </div>
        )}
      </Modal>

      {/* DOCUMENT MODAL */}
      <Modal
        open={documentModal}
        onCancel={() => setDocumentModal(false)}
        footer={null}
        width={700}
        title="Submitted Document"
      >
        <Table
          columns={documentColumns}
          dataSource={selectedDocuments}
          rowKey={(record, index) => index}
          pagination={false}
        />
      </Modal>



      {/* REVIEW MODAL (Authoriser) */}
      <Modal
        open={reviewModal}
        onCancel={() => setReviewModal(false)}
        footer={null}
        title="Authorise KYC"
      >
        {/* Reviewer recommendation block */}
        {selectedUser?.maker_decision && (
          <div
            style={{
              background: selectedUser.maker_decision === "RECOMMEND_APPROVE" ? "#f0fdf4" :
                selectedUser.maker_decision === "RECOMMEND_REJECT" ? "#fef2f2" : "#fefce8",
              border: `1px solid ${
                selectedUser.maker_decision === "RECOMMEND_APPROVE" ? "#bbf7d0" :
                selectedUser.maker_decision === "RECOMMEND_REJECT" ? "#fecaca" : "#fef08a"
              }`,
              borderRadius: 10,
              padding: "12px 16px",
              marginBottom: 18,
            }}
          >
            <div style={{ fontWeight: 700, marginBottom: 4 }}>
              {selectedUser.maker_decision === "RECOMMEND_APPROVE" && "✅ Reviewer Recommends: Approve"}
              {selectedUser.maker_decision === "RECOMMEND_REJECT" && "❌ Reviewer Recommends: Reject"}
              {selectedUser.maker_decision === "SUSPICIOUS" && "⚠️ Reviewer Flagged: Suspicious"}
            </div>
            {selectedUser.maker_comment && (
              <div style={{ color: "#4b5563", fontSize: 13 }}>
                <strong>Notes:</strong> {selectedUser.maker_comment}
              </div>
            )}
          </div>
        )}

        <Form layout="vertical" form={form} onFinish={handleReviewSubmit}
          onValuesChange={(changedValues) => {
            if (changedValues.decision === "Completed") {
              form.setFieldsValue({ reason: undefined });
            }
          }}>
          <Form.Item
            name="decision"
            label="Decision"
            rules={[{ required: true }]}
          >
            <Select placeholder="Select Decision">
              <Select.Option value="Completed">Complete</Select.Option>
              <Select.Option value="Rejected">Reject</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item shouldUpdate>
            {({ getFieldValue }) =>
              getFieldValue("decision") === "Rejected" && (
                <Form.Item
                  name="reason"
                  label="Rejection Reason"
                  rules={[{ required: true, message: "Please enter rejection reason" }]}
                >
                  <Input.TextArea rows={3} />
                </Form.Item>
              )
            }
          </Form.Item>

          <Button type="primary" htmlType="submit" block >
            Submit Review
          </Button>
        </Form>
      </Modal>

      {/* KYB APPROVE MODAL */}
      <Modal
        open={kybApproveModal}
        onCancel={() => {
          setKybApproveModal(false);
          setRegistrationDoc(null);
        }}
        footer={null}
        centered
        width={520}
        destroyOnClose
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 42,
                height: 42,
                borderRadius: "50%",
                background: "linear-gradient(135deg, #1677ff, #69b1ff)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
                fontSize: 18,
                fontWeight: 600,
              }}
            >
              ✓
            </div>

            <div>
              <div
                style={{
                  fontSize: 18,
                  fontWeight: 700,
                  color: "#111827",
                }}
              >
                Approve Merchant KYB
              </div>

              <div
                style={{
                  fontSize: 13,
                  color: "#6b7280",
                  marginTop: 2,
                }}
              >
                Upload registration proof before approval
              </div>
            </div>
          </div>
        }
      >
        {/* Reviewer recommendation block */}
        {selectedUser?.maker_decision && (
          <div
            style={{
              background: selectedUser.maker_decision === "RECOMMEND_APPROVE" ? "#f0fdf4" :
                selectedUser.maker_decision === "RECOMMEND_REJECT" ? "#fef2f2" : "#fefce8",
              border: `1px solid ${
                selectedUser.maker_decision === "RECOMMEND_APPROVE" ? "#bbf7d0" :
                selectedUser.maker_decision === "RECOMMEND_REJECT" ? "#fecaca" : "#fef08a"
              }`,
              borderRadius: 10,
              padding: "12px 16px",
              marginBottom: 18,
            }}
          >
            <div style={{ fontWeight: 700, marginBottom: 4 }}>
              {selectedUser.maker_decision === "RECOMMEND_APPROVE" && "✅ Reviewer Recommends: Approve"}
              {selectedUser.maker_decision === "RECOMMEND_REJECT" && "❌ Reviewer Recommends: Reject"}
              {selectedUser.maker_decision === "SUSPICIOUS" && "⚠️ Reviewer Flagged: Suspicious"}
            </div>
            {selectedUser.maker_comment && (
              <div style={{ color: "#4b5563", fontSize: 13 }}>
                <strong>Notes:</strong> {selectedUser.maker_comment}
              </div>
            )}
          </div>
        )}
        <Form layout="vertical" onFinish={handleKybApprove}>
          <div
            style={{
              background: "#f8fafc",
              border: "1px solid #e2e8f0",
              borderRadius: 18,
              padding: 20,
            }}
          >
            <Form.Item
              label={
                <span
                  style={{
                    fontWeight: 600,
                    fontSize: 14,
                    color: "#374151",
                  }}
                >
                  Registration Document
                </span>
              }
              extra="Supported formats: JPG, PNG, PDF"
              style={{ marginBottom: 0 }}
            >
              <label
                htmlFor="registration-upload"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexDirection: "column",
                  gap: 10,
                  width: "100%",
                  minHeight: 150,
                  borderRadius: 18,
                  cursor: "pointer",
                  background:
                    "linear-gradient(135deg, #003A7A 0%, #0052A8 50%, #0A6BDB 100%)",
                  color: "#fff",
                  border: "2px dashed rgba(255,255,255,0.35)",
                  boxShadow: "0 12px 30px rgba(0,58,122,0.35)",
                  transition: "all 0.3s ease",
                  position: "relative",
                  overflow: "hidden",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow =
                    "0 18px 35px rgba(0,58,122,0.45)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0px)";
                  e.currentTarget.style.boxShadow =
                    "0 12px 30px rgba(0,58,122,0.35)";
                }}
              >
                <div
                  style={{
                    width: 68,
                    height: 68,
                    borderRadius: "50%",
                    background: "rgba(255,255,255,0.18)",
                    backdropFilter: "blur(8px)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "1px solid rgba(255,255,255,0.25)",
                  }}
                >
                  <UploadOutlined style={{ fontSize: 30, color: "#fff" }} />
                </div>

                <div
                  style={{
                    fontSize: 18,
                    fontWeight: 700,
                    letterSpacing: 0.3,
                  }}
                >
                  Upload Document
                </div>

                <div
                  style={{
                    fontSize: 13,
                    opacity: 0.92,
                  }}
                >
                  Click here to choose your file
                </div>

                <input
                  id="registration-upload"
                  type="file"
                  accept="image/*,application/pdf"
                  onChange={(e) => setRegistrationDoc(e.target.files[0])}
                  style={{ display: "none" }}
                />
              </label>

              {registrationDoc && (
                <div
                  style={{
                    marginTop: 16,
                    padding: "12px 16px",
                    borderRadius: 12,
                    background: "#ecfdf3",
                    border: "1px solid #bbf7d0",
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                  }}
                >
                  <div
                    style={{
                      width: 34,
                      height: 34,
                      borderRadius: "50%",
                      background: "#22c55e",
                      color: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: 700,
                    }}
                  >
                    ✓
                  </div>

                  <div>
                    <div
                      style={{
                        fontSize: 13,
                        color: "#166534",
                        fontWeight: 600,
                      }}
                    >
                      File Selected Successfully
                    </div>

                    <div
                      style={{
                        fontSize: 12,
                        color: "#15803d",
                      }}
                    >
                      {registrationDoc.name}
                    </div>
                  </div>
                </div>
              )}
            </Form.Item>
          </div>

          <div
            style={{
              display: "flex",
              gap: 12,
              marginTop: 30,
            }}
          >
            <Button
              size="large"
              onClick={() => {
                setKybApproveModal(false);
                setRegistrationDoc(null);
              }}
              style={{
                flex: 1,
                borderRadius: 12,
                height: 48,
                fontWeight: 600,
              }}
            >
              Cancel
            </Button>

            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              size="large"
              style={{
                flex: 1,
                borderRadius: 12,
                height: 48,
                fontWeight: 700,
                background: "linear-gradient(135deg, #1677ff, #4096ff)",
                border: "none",
                boxShadow: "0 10px 24px rgba(22,119,255,0.25)",
              }}
            >
              Approve Merchant
            </Button>
          </div>
        </Form>
      </Modal>

      {/* MAKER REVIEW MODAL */}
      <Modal
        open={makerModal}
        onCancel={() => {
          setMakerModal(false);
          makerForm.resetFields();
        }}
        footer={null}
        title={selectedUser?.maker_decision ? "Reviewer Recommendation Details" : (makerModalType === "kyc" ? "Reviewer: Add KYC Recommendation" : "Reviewer: Mark KYB Under Review")}
        width={480}
        destroyOnClose
      >
        <p style={{ color: "#6b7280", marginBottom: 20, fontSize: 13 }}>
          {selectedUser?.maker_decision ? "This profile has already been reviewed by the Reviewer." : "Leave a recommendation and notes for the Authoriser who will make the final decision."}
        </p>
        <Form layout="vertical" form={makerForm} onFinish={handleMakerSubmit}>
          <Form.Item
            name="maker_decision"
            label="Recommendation"
            rules={[{ required: true, message: "Please select a recommendation" }]}
          >
            <Select placeholder="Select recommendation" disabled={!!selectedUser?.maker_decision}>
              <Select.Option value="RECOMMEND_APPROVE">✅ Recommend Approve</Select.Option>
              <Select.Option value="RECOMMEND_REJECT">❌ Recommend Reject</Select.Option>
              <Select.Option value="SUSPICIOUS">⚠️ Flag as Suspicious</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="maker_comment"
            label="Notes for Authoriser (optional)"
          >
            <Input.TextArea
              rows={4}
              placeholder="E.g. Documents look valid, ID matches. / Suspicious - name mismatch on document."
              disabled={!!selectedUser?.maker_decision}
            />
          </Form.Item>

          {!selectedUser?.maker_decision && (
            <Button type="primary" htmlType="submit" block size="large">
              {makerModalType === "kyc" ? "Submit Recommendation" : "Submit & Mark Under Review"}
            </Button>
          )}
        </Form>
      </Modal>

      {/* View User/Merchant Profile Modals from User Manager */}
      {currentAdminRole?.can_view && (activeTab === "kyc" ? (
        <UserProfileModal
          open={profileModalVisible && profileModalType === "view"}
          onClose={() => setProfileModalVisible(false)}
          onEdit={() => { }} // Not needed here as per request
          user={profileUser}
          loading={loading}
          mapStatusToBoolean={mapStatusToBoolean}
          lang={lang}
          onViewWallet={() => { }} // Wallet view not explicitly requested but component might need it
          ownerType={"users"}
        />
      ) : (
        <MerchantProfileModal
          open={profileModalVisible && profileModalType === "view"}
          onClose={() => setProfileModalVisible(false)}
          onEdit={() => { }}
          merchant={profileUser}
          loading={loading}
          lang={lang}
          onViewWallet={() => { }}
          onUpdateAdminComments={() => { }}
          adminCommentsUpdateLoading={false}
        />
      ))}
    </div>
  );
}

export default KYCManager;
